import { ChangeDetectorRef, ContentChild, Component, ElementRef, EventEmitter, HostBinding, Input, Inject, Optional, Renderer2, isDevMode } from '@angular/core';
import { RTL } from '@progress/kendo-angular-l10n';
import { TextBoxDirective } from './textbox.directive';
import { TextAreaDirective } from './textarea.directive';
import { NgControl } from '@angular/forms';
import { guid } from '../common/dom-utils';
var isFunction = function (x) { return Object.prototype.toString.call(x) === '[object Function]'; };
/**
 * Represents the Kendo UI TextBoxContainer component for Angular.
 * Provides floating labels to `input` elements.
 *
 * @example
 * ```ng-template
 * <kendo-textbox-container floatingLabel="First name">
 *   <input kendoTextBox />
 * </kendo-textbox-container>
 * ```
 */
var TextBoxContainerComponent = /** @class */ (function () {
    function TextBoxContainerComponent(elementRef, renderer, changeDetectorRef, rtl) {
        this.elementRef = elementRef;
        this.renderer = renderer;
        this.changeDetectorRef = changeDetectorRef;
        /**
         * @hidden
         */
        this.focused = false;
        /**
         * @hidden
         */
        this.empty = true;
        /**
         * @hidden
         */
        this.invalid = false;
        this._subscriptions = [];
        this.direction = rtl ? 'rtl' : 'ltr';
        this.renderer.removeAttribute(this.elementRef.nativeElement, "id");
    }
    Object.defineProperty(TextBoxContainerComponent.prototype, "hostClasses", {
        get: function () {
            return true;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TextBoxContainerComponent.prototype, "textareaElementClass", {
        get: function () {
            return !!this.textarea;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TextBoxContainerComponent.prototype, "focusedClass", {
        get: function () {
            return this.focused;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TextBoxContainerComponent.prototype, "emptyClass", {
        get: function () {
            return this.empty;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TextBoxContainerComponent.prototype, "invalidClass", {
        get: function () {
            return this.invalid;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @hidden
     */
    TextBoxContainerComponent.prototype.ngAfterContentInit = function () {
        var _this = this;
        if (!this.formControl && !this.textbox && !this.textarea) {
            if (isDevMode()) {
                throw new Error("The TextBoxContainer requires an element with the kendoTextBox or kendoTextArea directive" +
                    " or a forms-bound component to function properly.");
            }
            return;
        }
        var control = this.textbox || this.textarea || this.formControl.valueAccessor;
        var setFocus = function (isFocused) { return function () {
            _this.focused = isFocused;
            _this.updateState();
            _this.changeDetectorRef.markForCheck();
        }; };
        if (control.onFocus instanceof EventEmitter) {
            var focus_1 = control.onFocus.subscribe(setFocus(true));
            this._subscriptions.push(focus_1);
        }
        if (control.onBlur instanceof EventEmitter) {
            var blur_1 = control.onBlur.subscribe(setFocus(false));
            this._subscriptions.push(blur_1);
        }
        var updateState = function () { return _this.updateState(); };
        updateState();
        if (this.formControl) {
            var s = this.formControl.valueChanges.subscribe(updateState);
            this._subscriptions.push(s);
        }
        if ('id' in control) {
            if (this.id && control.id) {
                // input wins
                this.id = control.id;
            }
            else if (this.id) {
                control.id = this.id;
            }
            else if (control.id) {
                this.id = control.id;
            }
            else {
                var id = "_" + guid();
                control.id = id;
                this.id = id;
            }
        }
    };
    /**
     * @hidden
     */
    TextBoxContainerComponent.prototype.ngOnDestroy = function () {
        this._subscriptions.forEach(function (s) { return s.unsubscribe(); });
        this._subscriptions = [];
    };
    TextBoxContainerComponent.prototype.updateState = function () {
        var formControl = this.formControl;
        var empty = function (value) {
            // zero is not an empty value (e.g. NumericTextBox)
            if (value === 0) {
                return false;
            }
            // empty arrays are an empty value (e.g. MultiSelect)
            if (Array.isArray(value) && !value.length) {
                return true;
            }
            return !value;
        };
        if (formControl) {
            var valueAccessor = formControl.valueAccessor;
            if (isFunction(valueAccessor.isEmpty)) {
                this.empty = valueAccessor.isEmpty();
            }
            else {
                this.empty = empty(formControl.value);
            }
            this.invalid = formControl.invalid && (formControl.touched || formControl.dirty);
        }
        else if (this.textarea) {
            this.empty = empty(this.textarea.value);
        }
        else {
            this.empty = empty(this.textbox.value);
        }
        this.changeDetectorRef.markForCheck();
    };
    TextBoxContainerComponent.decorators = [
        { type: Component, args: [{
                    selector: 'kendo-textbox-container',
                    template: "\n        <ng-content></ng-content>\n        <label *ngIf=\"floatingLabel\" [for]=\"id\" class=\"k-label\">{{ floatingLabel }}</label>\n    "
                },] },
    ];
    /** @nocollapse */
    TextBoxContainerComponent.ctorParameters = function () { return [
        { type: ElementRef, },
        { type: Renderer2, },
        { type: ChangeDetectorRef, },
        { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [RTL,] },] },
    ]; };
    TextBoxContainerComponent.propDecorators = {
        'hostClasses': [{ type: HostBinding, args: ['class.k-textbox-container',] },],
        'textareaElementClass': [{ type: HostBinding, args: ['class.k-textarea-wrapper',] },],
        'focusedClass': [{ type: HostBinding, args: ['class.k-state-focused',] },],
        'emptyClass': [{ type: HostBinding, args: ['class.k-state-empty',] },],
        'invalidClass': [{ type: HostBinding, args: ['class.k-state-invalid',] },],
        'direction': [{ type: HostBinding, args: ['attr.dir',] },],
        'id': [{ type: Input },],
        'floatingLabel': [{ type: Input },],
        'textbox': [{ type: ContentChild, args: [TextBoxDirective,] },],
        'textarea': [{ type: ContentChild, args: [TextAreaDirective,] },],
        'formControl': [{ type: ContentChild, args: [NgControl,] },],
    };
    return TextBoxContainerComponent;
}());
export { TextBoxContainerComponent };



//////////////////
// WEBPACK FOOTER
// C:/spro/Workspaces/VisualCode/Ecoltex2/node_modules/@progress/kendo-angular-inputs/dist/es/textbox/textbox-container.component.js
// module id = ../../../../@progress/kendo-angular-inputs/dist/es/textbox/textbox-container.component.js
// module chunks = vendor